interface CheckoutRedirectUrlParams {
  communityRootUrl?: string;
  currentOrigin: string;
  eventRedirectUrl?: string;
  paywallRedirectUrl?: string;
  returnTo?: string;
}

const getSafeReturnTo = ({
  returnTo,
  communityRootUrl,
  currentOrigin,
}: CheckoutRedirectUrlParams): string | undefined => {
  if (
    typeof returnTo !== "string" ||
    returnTo.length === 0 ||
    returnTo !== returnTo.trim() ||
    /[\\\u0000-\u0020\u007f]/.test(returnTo)
  ) {
    return;
  }

  const isRootRelative = returnTo.startsWith("/") && !returnTo.startsWith("//");
  const absoluteMatch = returnTo.match(/^https?:\/\/([^/?#]+)/i);
  if (!isRootRelative && !absoluteMatch) return;

  try {
    const target = new URL(returnTo, currentOrigin);
    const authority = absoluteMatch?.[1].toLowerCase();
    const defaultPort = target.protocol === "https:" ? "443" : "80";
    const hasUnambiguousAuthority =
      isRootRelative ||
      authority === target.host ||
      authority === `${target.hostname}:${target.port || defaultPort}`;
    const communityOrigin = communityRootUrl
      ? new URL(communityRootUrl).origin
      : currentOrigin;
    if (
      ["http:", "https:"].includes(target.protocol) &&
      hasUnambiguousAuthority &&
      !target.username &&
      !target.password &&
      [currentOrigin, communityOrigin].includes(target.origin)
    ) {
      return target.href;
    }
  } catch {
    // Invalid caller-controlled values are ignored; trusted fallbacks still apply.
  }
  return undefined;
};

export const getCheckoutRedirectUrl = ({
  returnTo,
  eventRedirectUrl,
  paywallRedirectUrl,
  communityRootUrl,
  currentOrigin,
}: CheckoutRedirectUrlParams): string | undefined => {
  const safeReturnTo = getSafeReturnTo({
    returnTo,
    communityRootUrl,
    currentOrigin,
  });
  return (
    safeReturnTo || eventRedirectUrl || paywallRedirectUrl || communityRootUrl
  );
};
